import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { ArrowLeft, BookOpen, Heart } from 'lucide-react';

export default function EmpathyIsNot() {
  const navigate = useNavigate();

  const empathyIs = [
    'Present, open-hearted attention',
    'Listening for feelings and needs',
    'Connecting with what\'s alive in the other person',
    'Being fully with someone without judgment',
    'Sensing into another\'s experience',
    'Creating space for someone to be heard',
    'Compassionate presence'
  ];

  const empathyIsNot = [
    'Giving advice or trying to fix',
    'Telling your own story',
    'One-upping ("That\'s nothing, wait til you hear what happened to me...")',
    'Explaining or justifying',
    'Consoling ("There, there, it\'ll be okay")',
    'Interrogating or investigating',
    'Analyzing or diagnosing',
    'Educating or correcting',
    'Sympathizing or pitying',
    'Shutting down feelings'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="border-b border-slate-700/50 bg-slate-900/90 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/belonging-ebook')}
                variant="outline"
                size="sm"
                className="border-slate-600 hover:border-slate-500 text-white hover:bg-slate-800"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Guide
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-white">Empathy is... Empathy is not...</h1>
              </div>
            </div>
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <BookOpen className="h-4 w-4" />
              <span>Page 69</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 py-12">
        <div className="max-w-6xl mx-auto">
          {/* Introduction */}
          <div className="text-center mb-12">
            <Heart className="h-20 w-20 text-rose-400 mx-auto mb-6" />
            <h2 className="text-4xl font-bold text-white mb-4">Understanding True Empathy</h2>
            <p className="text-xl text-slate-300 leading-relaxed max-w-3xl mx-auto">
              Empathy is often misunderstood. Knowing what empathy is NOT helps us practice what it truly IS.
            </p>
          </div>

          {/* Two Column Comparison */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            {/* Empathy IS */}
            <div className="bg-gradient-to-br from-green-900/20 to-emerald-900/20 border border-green-700/30 rounded-lg overflow-hidden">
              <div className="bg-green-900/40 px-6 py-4 border-b border-green-700/30">
                <h3 className="text-2xl font-bold text-green-100 flex items-center gap-2">
                  <span className="text-3xl">✅</span>
                  Empathy IS...
                </h3>
              </div>
              <div className="p-6">
                <div className="space-y-3">
                  {empathyIs.map((item, idx) => (
                    <div
                      key={idx}
                      className="bg-green-900/30 border border-green-700/20 rounded-lg p-4 hover:bg-green-900/40 transition-colors"
                    >
                      <p className="text-green-100 leading-relaxed">{item}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Empathy is NOT */}
            <div className="bg-gradient-to-br from-red-900/20 to-orange-900/20 border border-red-700/30 rounded-lg overflow-hidden">
              <div className="bg-red-900/40 px-6 py-4 border-b border-red-700/30">
                <h3 className="text-2xl font-bold text-red-100 flex items-center gap-2">
                  <span className="text-3xl">❌</span>
                  Empathy is NOT...
                </h3>
              </div>
              <div className="p-6">
                <div className="space-y-3">
                  {empathyIsNot.map((item, idx) => (
                    <div
                      key={idx}
                      className="bg-red-900/30 border border-red-700/20 rounded-lg p-4 hover:bg-red-900/40 transition-colors"
                    >
                      <p className="text-red-100 leading-relaxed">{item}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Teaching Notes */}
          <div className="bg-blue-900/20 border border-blue-700/30 rounded-lg p-8">
            <h3 className="text-xl font-bold text-white mb-4">Teaching Notes</h3>
            <div className="space-y-4 text-slate-200">
              <p className="leading-relaxed">
                <span className="font-semibold text-white">Common Mistake:</span> Many people think they\'re offering 
                empathy when they\'re actually giving advice, sharing their own story, or trying to make someone feel better.
              </p>
              <p className="leading-relaxed">
                <span className="font-semibold text-white">True Empathy:</span> Simply being present with another person\'s 
                experience without trying to change it, fix it, or make it go away. It\'s about understanding, not solving.
              </p>
              <p className="leading-relaxed">
                <span className="font-semibold text-white">Practice Tip:</span> Have students practice in pairs. One person shares 
                something difficult. The other practices pure empathy—just reflecting feelings and needs without advice, 
                stories, or consolation.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
